<script>
import * as customProfileApi from '@/services/rest-api-sms/lib/custom-profile/custom-profile'
import * as sensorTypeApi from '@/services/rest-api-sms/lib/sensor-type/sensor-type'

export default {
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: null,
    },
    deviceCode: {
      type: [String, Number],
      default: null,
    },
    deviceTypes: {
      type: [],
      default: null,
    },
    imei: {
      type: [String, Number],
      default: null,
    },
    region: {
      type: String,
      default: 'SG',
    },
  },
  data() {
    return {
      loadingProfiles: false,
      saving: false,
      checking: false,
      sendingCommandId: null,
      profiles: [],
      checkResults: [],
      form: {
        profile: null,
        status: null,
      },
      savedProfile: null,
      checked: false,
      statusOptions: [
        { id: "enable", name: 'Enable' },
        { id: "disable", name: 'Disable' },
      ],
      sensorTypes: []
    }
  },
  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(value) {
        this.$emit('update:modal', value)
      },
    },
    companyId() {
      return (
        this.device?.company_id ||
        null
      )
    },
    selectedProfile() {
      return this.profiles.find(
        (profile) => this.getProfileId(profile) === this.getProfileId(this.form.profile)
      )
    },
    canSave() {
      return this.form.profile && this.form.status !== null && !this.saving
    },
    canCheckProfile() {
      return this.savedProfile && this.checkProfileDeviceId && !this.checking
    },
    savedStatusLabel() {
      return this.savedProfile?.status === false ? 'Disable' : 'Enable'
    },
    savedStatusValue() {
      return this.savedProfile?.status === false ? 'disable' : 'enable'
    },
    vehicleId() {
      return this.device?.vehicle_id || null
    },
    checkProfileDeviceId() {
      return this.device?.vehicle_id || this.device?.id || this.imei || null
    },
    commandGroups() {
      return this.checkResults.map((command) => {
        return {
          id:
            command.custom_profile_command_id ||
            `${command.device_code || this.deviceCode}-${command.sensor_code || 'empty'}`,
          title: this.buildCommandTitle(command),
          command: command.command || '-',
          messages: command.messages || [],
          is_match: command.is_valid === true,
          sendCommand: this.getSetCommand(command) || command,
        }
      })
    },
    mismatchCount() {
      return this.commandGroups.filter((command) => !command.is_match).length
    },
  },
  watch: {
    showModal(value) {
      if (value) {
        this.resetForm()
        this.fetchProfiles()
      }
    },
  },
  mounted() {
    if (this.showModal) {
      this.fetchProfiles()
      this.getSensorTypes()
    }
  },
  methods: {
    async fetchProfiles() {
      if (!this.companyId || !this.deviceCode) {
        this.profiles = []
        return
      }

      this.loadingProfiles = true
      try {
        let filters = [`device_code eq ${this.deviceCode}`]

        if (this.companyId !== Number(process.env?.VUE_APP_COMPANY_ID_MCEASY)) {
          filters.push(`company_id eq ${this.companyId}`)
        }
        const response = await customProfileApi.get({ filters: filters, limit: 100000 }, this.region)
        this.profiles = response?.data?.paginated_result || []
        this.form.profile = this.profiles.find((profile) => profile?.custom_profile_id === this.device?.custom_profile_id) || null
        if (this.form.profile) {
          this.savedProfile = { profile: this.form.profile, status: 'success' }
        } else if (this.device?.custom_profile_id && !this.form.profile) {
          this.savedProfile = { profile: { name: 'deleted' }, status: 'error' }
        }
      } catch (error) {
        this.profiles = []
        this.$toast.error(
          error?.response?.data?.message || error?.message || 'Gagal mengambil custom profile'
        )
      } finally {
        this.loadingProfiles = false
      }
      let profiles = null
      if (this.device?.custom_profile_id) {
        profiles = this.profiles.find((profile) => profile?.custom_profile_id === this.device?.custom_profile_id)
        this.handleGetProfileData()
      } else if (this.companyId !== Number(process.env?.VUE_APP_COMPANY_ID_MCEASY)) {
        profiles = this.profiles.find((profile) => profile?.type === "PRIMARY")
      }

      this.form.profile = profiles
    },

    async getSensorTypes() {
      try {
        let params = {
          limit: 100000,
          page: 1,
        }
        let sensorTypeResponse = await sensorTypeApi.get(params, this.region)
        this.sensorTypes = sensorTypeResponse?.data?.paginated_result
      } catch (error) {
        this.handleError(error)
      }
    },

    getProfileId(profile) {
      return profile?.custom_profile_id || profile?.id || null
    },
    buildCommandTitle(command) {
      const deviceCode = this.deviceTypes.find((dc) => dc.device_code === command.device_code)
      const sensorCode = this.sensorTypes.find((sensor) => sensor.sensor_code === command.sensor_code)
      return `${deviceCode?.device_name ?? 'Device Not Found'} - ${sensorCode?.sensor_name ?? 'Device Setting'}`
    },
    resetCheckState() {
      this.checked = false
      this.checkResults = []
    },
    resetForm() {
      this.form = {
        profile: null,
        status: null,
      }
      this.resetCheckState()
    },
    async handleSave() {
      if (!this.canSave) return

      this.saving = true
      try {
        const profile = this.selectedProfile || this.form.profile
        await customProfileApi.updateVehicleProfile(
          this.vehicleId,
          {
            custom_profile_id: this.getProfileId(profile),
            status: this.form.status
          },
          this.region
        )
        
        this.checked = false
        this.checkResults = []
        if (this.form.status === "enable") {
          this.savedProfile = {
            profile,
            status: 'success',
          }

          this.$toast.success('Custom profile tersimpan')
        } else {
          this.savedProfile = null
          this.$toast.success('Custom profile disable')
        }
        this.$emit('beforeSubmit')
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || 'Gagal menyimpan custom profile'
        )
      } finally {
        this.saving = false
      }
    },
    async handleCheckProfile() {
      this.checking = true
      try {
        const payload = {
          type: 'GET',
          status: this.savedStatusValue,
        }

        await customProfileApi.checkProfile(this.checkProfileDeviceId, payload, this.region)
        await this.delay(3000)

        await this.handleGetProfileData()
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || 'Gagal cek profile'
        )
      } finally {
        this.checking = false
      }
    },

    async handleGetProfileData() {
      try {
        const payload = {
          type: 'GET',
          status: this.savedStatusValue,
        }

        const response = await customProfileApi.getCheckProfile(this.checkProfileDeviceId, payload, this.region)
        this.checkResults = response?.data || []
        this.checked = true
      }
      catch (error) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || 'Gagal get data profile'
        )
      }
    },
    async handleSendCommand(command) {
      if (!this.vehicleId || !command?.sendCommand?.command) return

      this.checking = true
      this.sendingCommandId = command.id
      try {
        await customProfileApi.resendCommand(
          {
            vehicle_id: this.vehicleId,
            device_command: command.command,
            device_code: command.sendCommand.device_code || this.deviceCode,
          },
          this.region
        )
        this.$toast.success('Success Send')
        await this.delay(3000)

        const payload = {
          type: 'GET',
          status: this.savedStatusValue,
        }
        const response = await customProfileApi.getCheckProfile(this.checkProfileDeviceId, payload, this.region)
        this.checkResults = response?.data || []
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || 'Gagal kirim command'
        )
      } finally {
        this.checking = false
        this.sendingCommandId = null
      }
    },
    closeModal() {
      this.showModal = false
    },
    getSetCommand(command) {
      const commands = this.savedProfile?.profile?.commands || []
      return commands.find((profileCommand) => {
        return (
          profileCommand.command_type === 'SET' &&
          profileCommand.device_code === command.device_code &&
          profileCommand.sensor_code === command.sensor_code
        )
      })
    },
    delay(milliseconds) {
      return new Promise((resolve) => setTimeout(resolve, milliseconds))
    },
  },
}
</script>

<template>
  <div>
    <b-modal
      v-model="showModal"
      class="mc-modal custom-profile-command-modal"
      id="modal-custom-profile-command"
      title="Custom profile"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <div class="custom-profile-command-modal__body">
        <b-form @submit.stop.prevent="handleSave">
          <b-form-group label="Profile" label-for="custom-profile-select" class="mb-3">
            <v-select
              id="custom-profile-select"
              v-model="form.profile"
              :options="profiles"
              :loading="loadingProfiles"
              placeholder="Pilih Profile"
              label="name"
              :clearable="false"
              @input="resetCheckState"
            />
            <span v-if="form.profile" class="mt-1" :style="{ fontSize: '12px', marginLeft: '5px' }">domain: {{ form.profile.domain }}</span>
          </b-form-group>

          <b-form-group label="Status profile" label-for="custom-profile-status">
            <v-select
              id="custom-profile-status"
              v-model="form.status"
              :options="statusOptions"
              placeholder="Pilih Status"
              label="name"
              :reduce="(status) => status.id"
              :clearable="false"
              @input="resetCheckState"
            />
          </b-form-group>

          <div class="custom-profile-command-modal__save">
            <b-button variant="primary" type="submit" :disabled="!canSave">
              <b-spinner v-if="saving" small type="grow"></b-spinner>
              {{ saving ? 'Loading...' : 'Simpan' }}
            </b-button>
          </div>
        </b-form>

        <div v-if="savedProfile" :class="'custom-profile-command-modal__' + savedProfile?.status">
          <i class="mc-fa icon-outline_check"></i>
          Profile {{savedProfile?.profile?.name }}{{ savedStatusLabel && '' }}.
        </div>

        <hr />

        <section class="custom-profile-command-modal__check">
          <h5>Cek profile</h5>
          <p>
            Kirim command get untuk membandingkan pengaturan perangkat dengan profile yang
            tersimpan.
          </p>

          <b-button variant="outline-primary" :disabled="!savedProfile" @click="handleCheckProfile">
            <i class="mc-fa icon-outline_cloud-upload"></i>
            <b-spinner v-if="checking" small type="grow"></b-spinner>
            {{ checking ? 'Loading...' : checked ? 'Cek ulang' : 'Cek profile' }}
          </b-button>
        </section>

        <section v-if="checked" class="custom-profile-command-modal__result mt-2">
          <p v-if="mismatchCount" class="custom-profile-command-modal__warning">
            {{ mismatchCount }} dari {{ commandGroups.length }} command belum sesuai - kirim command
            khusus untuk yang tidak sesuai.
          </p>

          <div class="custom-profile-command-modal__list">
            <div v-if="!commandGroups.length" class="custom-profile-command-modal__empty">
              Command profile tidak tersedia.
            </div>
            <div
              v-for="command in commandGroups"
              :key="command.id"
              class="custom-profile-command-modal__item"
              :class="{ 'custom-profile-command-modal__item--invalid': !command.is_match }"
            >
              <div>
                <strong>{{ command.title }}</strong>
                <span>{{ command.command }}</span>
                <small
                  v-for="(message, messageIndex) in command.messages"
                  :key="`${command.id}-${messageIndex}`"
                >
                  Profile: {{ message.Expected || '-' }}<br />
                  Perangkat:
                  {{ message.Found ? message.Message || message.Expected || '-' : message.Message || '-' }}
                </small>
                <small v-if="!command.messages.length">Detail profile tidak tersedia.</small>
              </div>

              <div class="custom-profile-command-modal__action">
                <span
                  class="custom-profile-command-modal__badge"
                  :class="{ 'custom-profile-command-modal__badge--invalid': !command.is_match }"
                >
                  {{ command.is_match ? 'Sesuai' : 'Tidak sesuai' }}
                </span>
                <b-button
                  v-if="!command.is_match"
                  variant="outline-primary"
                  size="sm"
                  :disabled="sendingCommandId === command.id"
                  @click="handleSendCommand(command)"
                >
                  <i class="mc-fa icon-outline_cloud-upload"></i>
                  {{ sendingCommandId === command.id ? 'Loading...' : 'Kirim command' }}
                </b-button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="custom-profile-command-modal__footer">
        <b-button variant="outline-primary" @click="closeModal">Batal</b-button>
        <b-button variant="primary" :disabled="!savedProfile" @click="closeModal">Selesai</b-button>
      </div>
    </b-modal>
  </div>
</template>
